import React, { useState, useEffect } from 'react';
import { Button, Loading } from 'carbon-components-react';
import { CheckmarkOutline32, FaceNeutral32 } from '@carbon/icons-react';

const Completed: React.FC<Props> = ({
  importRecentEnquiries,
  onClickGoToLocateButton,
  importEnquiriesResponse,
  isImportingEnquiries,
  fetchImportEnquiriesStatus,
  isFetchingImportEnquiriesStatus
}) => {
  const [goButtonDisable, setGoButtonDisable] = useState<boolean>(true);
  const [enquiriesFound, setEnquiriesFound] = useState<number>(0);
  const [insertingEnquiries, setInsertingEnquires] = useState<string>('');
  const [insertingResponses, setInsertingResponses] = useState<string>('');

  useEffect(() => {
    if (
      importRecentEnquiries &&
      !isImportingEnquiries &&
      importEnquiriesResponse?.success
    ) {
      setEnquiriesFound(importEnquiriesResponse.totalEnquiriesToImport);
    } else if (!importRecentEnquiries) {
      setGoButtonDisable(false);
    }
  }, [isImportingEnquiries, importEnquiriesResponse, importRecentEnquiries]);

  useEffect(() => {
    if (!isFetchingImportEnquiriesStatus && fetchImportEnquiriesStatus) {
      if (fetchImportEnquiriesStatus.status === 'completed') {
        setInsertingEnquires('completed');
        setInsertingResponses('completed');
        setGoButtonDisable(false);
      } else if (fetchImportEnquiriesStatus.status !== 'completed') {
        setInsertingEnquires(
          fetchImportEnquiriesStatus.details?.importing_enquiries
        );
        setInsertingResponses(
          fetchImportEnquiriesStatus.details?.importing_responses
        );
      }
    }
  }, [isFetchingImportEnquiriesStatus, fetchImportEnquiriesStatus]);

  return (
    <div>
      <div className="form-fields-wrapper form-fields-wrapper-wide">
        <div className={`textinput-fields-wrapper padding step5`}>
          <div className="import-processing-info-container">
            {importRecentEnquiries && enquiriesFound > 0 && (
              <>
                <div className="import-processing-info-wrapper">
                  <div className="info-row-container">
                    <div className="info-row-image-1">
                      <div className="number-found">{enquiriesFound}</div>
                    </div>
                    <div className="info-row-description">
                      {`Found ${enquiriesFound} recent ${
                        enquiriesFound > 1 ? 'enquiries' : 'enquiry'
                      }`}
                    </div>
                  </div>
                  <div className="info-row-container">
                    <div className="info-row-image-2">
                      {insertingEnquiries === 'completed' && (
                        <CheckmarkOutline32 className="checkmark-custom-style" />
                      )}
                      {insertingEnquiries === 'started' && (
                        <Loading
                          className="loading-custom-style"
                          withOverlay={false}
                        />
                      )}
                    </div>
                    <div className="info-row-description">
                      {insertingEnquiries === 'completed'
                        ? `Loaded all enquiries`
                        : enquiriesFound > 1
                        ? `Fetching ${enquiriesFound} enquiries`
                        : `Fetching ${enquiriesFound} enquiry`}
                    </div>
                  </div>
                  {insertingResponses !== 'standby' && (
                    <div className="info-row-container">
                      <div className="info-row-image-2">
                        {insertingResponses === 'started' && (
                          <Loading
                            className="loading-custom-style"
                            withOverlay={false}
                          />
                        )}
                        {insertingResponses === 'completed' && (
                          <CheckmarkOutline32 className="checkmark-custom-style" />
                        )}
                      </div>
                      <div className="info-row-description">
                        {insertingResponses === 'completed'
                          ? `Loaded all response documents`
                          : `Fetching response documents`}
                        <br />
                        {insertingResponses === 'started' && (
                          <span onClick={() => onClickGoToLocateButton(true)}>
                            This may take some time. Skip and import in the
                            background.
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="import-processing-description">
                  Hold on while we import all the things for your map...
                </div>
              </>
            )}
            {importRecentEnquiries && enquiriesFound <= 0 && (
              <div className="no-enquiries-to-import">
                <div className="no-enquiries-to-import-image">
                  <FaceNeutral32 />
                </div>
                <div className="no-enquiries-to-import-wording-1">
                  We didn&#39;t find any matching enquiries to import
                </div>
              </div>
            )}
            {!importRecentEnquiries && (
              <div className="no-enquiries-to-import">
                <div className="no-enquiries-to-import-image">
                  <CheckmarkOutline32 />
                </div>
                <div className="no-enquiries-to-import-wording-2">
                  You are ready to start using SmarterWX Locate.
                  <br />
                  Click on the button below to proceed.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div id="formButtons" className="form-buttons-wrapper-step5 padding">
        <div>
          {importRecentEnquiries && enquiriesFound === 0 && (
            <>
              <div className="can-still-use-locate">
                You can still use SmarterWX Locate without loading previous
                enquiries
              </div>
            </>
          )}
          <Button
            kind="secondary"
            size={'md'}
            onClick={() => onClickGoToLocateButton(false)}
            disabled={goButtonDisable}
          >
            Start using SmarterWX Locate
          </Button>
        </div>
        <div className="decorative-image">
          <img alt="decorative" src="./shapes.svg" />
        </div>
      </div>
    </div>
  );
};

type Props = {
  importRecentEnquiries: boolean;
  onClickGoToLocateButton: Function;
  importEnquiriesResponse: any;
  isImportingEnquiries: boolean;
  fetchImportEnquiriesStatus: any;
  isFetchingImportEnquiriesStatus: boolean;
};

export default React.memo(Completed);
